import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import '../users/EditUser.css';

function EditUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo } = useAuth();

  const loggedRole = userInfo?.role || '';
  const isSingleUser = userInfo?.is_single_user;
  const userLocationIds = userInfo?.user_location_ids || [];

  const [values, setValues] = useState({
    name: '',
    email: '',
    role: '',
    location_ids: []
  });
  const [originalRole, setOriginalRole] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  axios.defaults.withCredentials = true;

  const canChooseLocations = loggedRole === 'super_admin' && !isSingleUser;
  const isLockedRole = originalRole === 'docent' || originalRole === 'ouder';

  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/location/allLocations`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const locs = res.data.data || res.data || [];
      setLocations(Array.isArray(locs) ? locs : []);
    } catch (err) {
      console.error('Error fetching locations', err);
      setLocations([]);
    }
  }, [token]);

  useEffect(() => {
    if (canChooseLocations) {
      fetchLocations();
    }
  }, [canChooseLocations, fetchLocations]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/user/get/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const usr = res.data.data;

        if (!usr) {
          alert('Gebruiker niet gevonden.');
          navigate('/users');
          return;
        }

        setValues({
          name: usr.name || '',
          email: usr.email || '',
          role: usr.role || '',
          location_ids: Array.isArray(usr.location_ids) ? usr.location_ids : []
        });

        setOriginalRole(usr.role || '');
      } catch (err) {
        console.error('Error fetching user data:', err);
        alert('Fout bij laden van gebruiker.');
        navigate('/users');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, token, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'role' && isLockedRole) {
      return;
    }

    setValues((prev) => {
      const updated = {
        ...prev,
        [name]: value
      };

      if (name === 'role' && value === 'locatie_admin' && prev.location_ids.length > 1) {
        updated.location_ids = [prev.location_ids[0]];
      }

      return updated;
    });
  };

  const handleLocationChange = (locId) => {
    setValues((prev) => {
      if (prev.role === 'locatie_admin') {
        return {
          ...prev,
          location_ids: [locId]
        };
      }

      const alreadySelected = prev.location_ids.includes(locId);

      return {
        ...prev,
        location_ids: alreadySelected
          ? prev.location_ids.filter((id) => id !== locId)
          : [...prev.location_ids, locId]
      };
    });
  };

  const validateForm = () => {
    if (!values.name.trim()) {
      alert('Naam is verplicht.');
      return false;
    }

    if (!values.role) {
      alert('Kies een rol.');
      return false;
    }

    if (canChooseLocations && values.role === 'locatie_admin' && values.location_ids.length !== 1) {
      alert('Een locatie admin moet precies één locatie hebben.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        name: values.name.trim(),
        role: isLockedRole ? originalRole : values.role,
        location_ids: []
      };

      if (canChooseLocations) {
        payload.location_ids = values.location_ids;
      } else if (loggedRole === 'locatie_admin') {
        payload.location_ids = userLocationIds;
      }

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/user/update/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      navigate('/users');
    } catch (err) {
      console.error('Error updating user', err);
      alert(err.response?.data?.message || 'Kon gebruiker niet bijwerken.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate('/users');

  if (loading) return <p>Loading...</p>;

  return (
    <div className="bg-white p-4 pb-xxl edit-user">
      <h2 className="text-center">Gebruiker Bewerken</h2>
      <hr className="separator" />

      <form onSubmit={handleSubmit}>
        <div className="row mb-3">
          <div className="col">
            <label>
              Naam <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="name"
              className="form-control rounded-0"
              required
              value={values.name}
              onChange={handleInputChange}
            />
          </div>

          <div className="col">
            <label>Email</label>
            <input
              type="email"
              className="form-control rounded-0"
              value={values.email}
              disabled
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col">
            <label>
              Rol <span className="text-danger">*</span>
            </label>
            <select
              name="role"
              className="form-select rounded-0"
              value={values.role}
              onChange={handleInputChange}
              required
              disabled={isLockedRole}
            >
              {isLockedRole ? (
                <option value={values.role}>
                  {values.role.charAt(0).toUpperCase() + values.role.slice(1)}
                </option>
              ) : (
                <>
                  <option value="">-- Kies rol --</option>

                  {loggedRole === 'super_admin' && (
                    <>
                      <option value="super_admin">Super Admin</option>
                      <option value="locatie_admin">Locatie Admin</option>
                    </>
                  )}

                  {loggedRole === 'locatie_admin' && (
                    <option value="locatie_admin">Locatie Admin</option>
                  )}
                </>
              )}
            </select>

            {isLockedRole && (
              <small className="text-muted d-block mt-1">
                De rol van een ouder of docent kan hier niet worden gewijzigd.
              </small>
            )}
          </div>
        </div>

        {canChooseLocations && (
          <div className="mb-3">
            <label>Koppel Locaties</label>

            {values.role === 'locatie_admin' && (
              <div className="text-muted mb-2">
                Een locatie admin kan slechts één locatie beheren.
              </div>
            )}

            <div className="d-flex flex-wrap">
              {Array.isArray(locations) && locations.length > 0 ? (
                locations.map((loc) => (
                  <div key={loc.location_id} className="form-check me-3 mb-2">
                    <input
                      type={values.role === 'locatie_admin' ? 'radio' : 'checkbox'}
                      name="location_select"
                      className="form-check-input"
                      id={`loc-${loc.location_id}`}
                      checked={values.location_ids.includes(loc.location_id)}
                      onChange={() => handleLocationChange(loc.location_id)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`loc-${loc.location_id}`}
                    >
                      {loc.location_name}
                    </label>
                  </div>
                ))
              ) : (
                <p>Geen locaties beschikbaar</p>
              )}
            </div>
          </div>
        )}

        <hr className="separator" />

        <div className="row">
          <div className="col-md-6 mb-2">
            <button
              type="button"
              className="btn btn-secondary w-100 rounded-0"
              onClick={handleCancel}
            >
              Annuleren
            </button>
          </div>

          <div className="col-md-6">
            <button
              type="submit"
              className="btn btn-primary w-100 rounded-0"
              disabled={loading}
            >
              {loading ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default EditUser;